// Shared add-on resource metadata (025-subscription-addons).
//
// One source of truth for the purchasable resources so labels, ordering,
// the metered/hard-cap split, and the stepper unit model (D7) stay consistent
// across every surface — the accordion sections, the cart summary, and the
// discovery entry points.
//
// Naming (ux-design.md, direction A): the `icon_uploads` resource is labelled
// "Custom Icons" in ALL user-facing copy (the internal key stays `icon_uploads`).
// Title Case because it names a product feature, so it stays capitalized
// mid-sentence the same way "Kits" does (CONTENT-05).
// `metered` distinguishes pageviews/bandwidth (overage-savings framing) from
// kits/icon_uploads (raise-the-ceiling framing).
//
// Stepper unit model (D7): the stepper shows the RESULTING resource total, not a
// pack count. It starts at the current plan amount (base plan limit + active
// add-on units) and steps by `increment`. `planField` names the base-limit
// attribute on the (deserialized) plan; all four are integers in their native
// display unit — kits/icons/pageviews are counts, bandwidth is GB (plan
// `monthlyBandwidth` is stored in GB). The `increment` values mirror the
// `add_on_products` catalog (FR-028); keep them in sync if the catalog changes.

import get from 'lodash/get'

import { formatPriceCents } from 'app/views/plans/priceUtils'

// `displayDivisor` + `displaySuffix` scale the stepper's shown number so large
// counts stay legible: bandwidth carries a "GB" unit, kits/icons show their raw
// count (no unit). A resource whose sensible unit depends on the plan declares
// `displayScale(baseUnits)` instead — see `displayScaleFor` below.
// `monthly` marks an allowance that refills each month rather than standing
// until it is changed. Declared rather than inferred from `metered`: the two
// happen to coincide across today's four resources, but they answer different
// questions — whether overage bills, and whether the figure is a rate. A surface
// quoting a total says so ("2.75M Pageviews/mo"), which a kit count must not.
// `iconName` is the resource's mark (a `$v7.solid` name) wherever a surface
// pictures it — the accordion sections, the cart summary, and the upsell
// modal's add-on option — so one resource can't wear two icons.
// `binary` marks an on/off add-on (Pro+): held or not, never counted, so it has
// no stepper and no plan limit (`planField: null`). `onPlansTable: false` keeps
// it out of the /plans comparison table, which offers it on its own card.
export const ADD_ON_RESOURCES = [
  {
    key: 'kits',
    label: 'Kits',
    iconName: 'faSuitcase',
    metered: false,
    monthly: false,
    increment: 5,
    planField: 'kits',
    displayDivisor: 1,
    displaySuffix: ''
  },
  {
    key: 'icon_uploads',
    label: 'Custom Icons',
    iconName: 'faCloudArrowUp',
    metered: false,
    monthly: false,
    increment: 100,
    planField: 'customIcons',
    displayDivisor: 1,
    displaySuffix: ''
  },
  {
    key: 'pageviews',
    label: 'Pageviews',
    iconName: 'faSignalBars',
    metered: true,
    monthly: true,
    increment: 250000,
    planField: 'monthlyPageviews',
    // Plans run from 50K (Starter) to 20M (Max Plus) — a 400× spread no single
    // unit reads well across. "0.05M" is a poor way to say fifty thousand, and
    // "20,000K" a poor way to say twenty million, so the unit follows the plan.
    displayScale: (baseUnits) => (baseUnits >= 1000000 ? { divisor: 1000000, suffix: 'M' } : { divisor: 1000, suffix: 'K' }),
    displayDivisor: 1000,
    displaySuffix: 'K'
  },
  {
    key: 'bandwidth',
    label: 'Bandwidth',
    iconName: 'faBox',
    metered: true,
    monthly: true,
    increment: 1,
    planField: 'monthlyBandwidth',
    displayDivisor: 1,
    displaySuffix: 'GB'
  },
  {
    key: 'pro_plus',
    label: 'Pro+ Icons',
    iconName: 'faShapes',
    metered: false,
    monthly: false,
    binary: true,
    chargedInFull: true,
    onPlansTable: false,
    increment: 1,
    planField: null,
    displayDivisor: 1,
    displaySuffix: ''
  }
]

// The resource keys, in the order the pickers list them. Every other
// module that needs to walk the resources derives from this rather than
// re-typing the list — `ADD_ON_RESOURCES` above holds objects, so a second
// declaration under a similar name is an easy thing to mistake for it.
export const ADD_ON_RESOURCE_KEYS = ADD_ON_RESOURCES.map((r) => r.key)

export const RESOURCE_LABELS = ADD_ON_RESOURCES.reduce((acc, r) => {
  acc[r.key] = r.label
  return acc
}, {})

export function resourceLabel(key) {
  return RESOURCE_LABELS[key] || key
}

export function resourceMeta(key) {
  return ADD_ON_RESOURCES.find((r) => r.key === key) || null
}

export function incrementFor(key) {
  const meta = resourceMeta(key)
  return meta ? meta.increment : 1
}

// Whether the resource bills overages (pageviews/bandwidth) rather than being a
// hard cap (kits/icon_uploads). Drives the framing copy on every add-on surface.
export function isMeteredFor(key) {
  return get(resourceMeta(key), 'metered', false)
}

// Whether the allowance refills monthly, so a surface quoting a total can mark
// it as a rate rather than a standing figure.
export function isMonthlyFor(key) {
  return get(resourceMeta(key), 'monthly', false)
}

// Whether the add-on costs its full price on the day it's bought, at every
// interval, rather than a prorated share on an annual plan. Pro+ only.
export function isChargedInFullFor(key) {
  return get(resourceMeta(key), 'chargedInFull', false)
}

// Whether the add-on is on/off (held or not) rather than counted in packs.
export function isBinaryFor(key) {
  return get(resourceMeta(key), 'binary', false)
}

// The Pro+ add-on price on the customer's plan, in cents, or null when the plan
// can't buy it. The server sends it only while Pro+ is on sale and this plan's
// price is billable, so a null here means "don't offer it". Read from the plan
// rather than the subscription so the plan-change screen, which swaps in the
// target plan, prices the plan being moved to. Every surface that shows the
// price reads it here; the price never appears in code or copy.
export function proPlusAddOnPriceFor(subscription) {
  return get(subscription, ['plan', 'proPlusAddOnPrice'], null)
}

// That price as every surface prints it, "$30/yr" on the plan's own interval,
// or null when the plan can't buy Pro+.
export function proPlusPriceLabel(subscription) {
  const cents = proPlusAddOnPriceFor(subscription)
  if (cents == null) return null

  return `${formatPriceCents(cents)}/${get(subscription, ['plan', 'annual'], true) ? 'yr' : 'mo'}`
}

// The metered-billing settings attribute that switches a resource's overage
// billing on, mirroring `isMeteredBillingPageviews`/`isMeteredBillingBandwidth`
// in the kits/bandwidth usage mixins.
const METERED_BILLING_SETTING = {
  pageviews: 'meteredPageviewsOn',
  bandwidth: 'meteredBandwidthOn'
}

const METERED_BILLING_RELEASED = {
  pageviews: () => FONTAWESOME.METERED_PAGEVIEWS_ACTIVE === 'yes',
  bandwidth: () => FONTAWESOME.METERED_BANDWIDTH_ACTIVE === 'yes'
}

// Whether overages for `key` are actually billed rather than cutting service
// off: the feature has to be released AND the customer has to have it switched
// on for that resource. Both conditions, same as the usage mixins — this lets
// the add-on surfaces read the setting without pulling those mixins in.
//
// `settings` is the `meteredBilling/meteredBillingSettings` getter, which falls
// back to the copy on the subscription for roles that can't read the endpoint.
export function isMeteredBillingOnFor(settings, key) {
  const attribute = METERED_BILLING_SETTING[key]
  if (!attribute) return false
  if (!METERED_BILLING_RELEASED[key]()) return false

  return get(settings, attribute, false) === true
}

// The value both the plan serializer and the effective limits use for "no
// ceiling" (`UsageLimits.unlimited/0` server-side).
export const UNLIMITED = -1

// Base plan limit for a resource, in native display units (kits/icons/pageviews
// counts, bandwidth GB). Read from the plan serializer, which already exposes
// `kits`, `customIcons`, `monthlyPageviews`, `monthlyBandwidth`.
export function baseUnitsFor(subscription, key) {
  const meta = resourceMeta(key)
  if (!meta || !meta.planField) return 0
  return get(subscription, ['plan', meta.planField], 0) || 0
}

// The unit the stepper counts this resource in: `{ divisor, suffix }`.
//
// Read from the PLAN's own limit rather than the customer's current selection,
// so the unit can't change under their fingers mid-step — a stepper that
// switched from K to M as it crossed a million would move the decimal point
// while they were deciding. The 1M threshold matches `compactThousands`, so the
// number in the pill and the amount in the copy beside it always agree.
//
// Resources with a fixed unit (kits, custom icons, bandwidth) declare
// `displayDivisor`/`displaySuffix` and never reach the plan-dependent branch.
export function displayScaleFor(subscription, key) {
  const meta = resourceMeta(key)
  if (!meta) return { divisor: 1, suffix: '' }

  if (meta.displayScale) return meta.displayScale(baseUnitsFor(subscription, key))

  return { divisor: meta.displayDivisor || 1, suffix: meta.displaySuffix || '' }
}

// Resources whose meaning depends on a plan capability, mapped to the (camelized)
// plan attribute that carries it. A resource absent from this table is universal.
//
// Mirrors `@plan_capability` in `Fontawesome.Subscriptions.AddOns`; the server
// refuses a purchase with `not_supported_by_plan`, this hides the section so the
// customer never gets that far. Keep the two in step.
const PLAN_CAPABILITY = {
  bandwidth: 'privateRegistriesEnabled'
}

// Whether `key` is meaningful on this customer's plan.
//
// Bandwidth is npm-registry download bandwidth: a CDN-only plan (Starter, and the
// monthly Solo/Team cycles) has no registry to download from, ships
// `monthlyBandwidth: 0`, and carries no bandwidth overage price — so capacity
// bought there is capacity that can never be spent. Offering the stepper would
// take money for nothing.
//
// Fail closed: a subscription whose plan hasn't loaded yet is offered nothing
// capability-gated, rather than briefly showing a section that then vanishes.
//
// Pro+ is offered where the plan carries a Pro+ add-on price, which the server
// sets only for plans that deliver Pro without Pro+ (Starter excluded) and only
// while the add-on is on sale. Mirrors `plan_supports?(_, :pro_plus)`.
export function isSupportedFor(subscription, key) {
  if (key === 'pro_plus') return proPlusAddOnPriceFor(subscription) != null

  const attribute = PLAN_CAPABILITY[key]
  if (!attribute) return true

  return get(subscription, ['plan', attribute], false) === true
}

// The resources this customer can actually be offered, in display order.
export function supportedResourcesFor(subscription) {
  return ADD_ON_RESOURCES.filter((resource) => isSupportedFor(subscription, resource.key))
}

// Whether the resource is already unlimited for this customer — Max and Max Plus
// ship unlimited kits and custom icons, and an admin override can set any
// resource that way.
//
// Nothing can be sold here: the server short-circuits an unlimited baseline and
// never adds purchased units to it, so a purchase would charge for capacity it
// can't grant. The section must say so rather than offer a stepper — one that
// starts at -1 and steps to 4 is both nonsense and a route to a dead purchase.
export function isUnlimitedFor(subscription, key) {
  return baseUnitsFor(subscription, key) === UNLIMITED || currentUnitsFor(subscription, key) === UNLIMITED
}

// Whether one add-on record is granting capacity at `at` (now by default).
//
// Mirrors the backend's entitlement filter (`AddOns.purchased_quantity/2`): a
// record scheduled for removal keeps granting until its `endsAt`, and stops
// counting after — the row is never deleted, so a lapsed one must be excluded
// here too or the client reports capacity the server no longer honors. A
// `scheduled` record is the mirror image: it grants nothing until the plan
// change that starts it, so it never counts here.
//
// `at` is a parameter rather than always `now` because the same question gets
// asked about a future instant — `planPhases` asks which add-ons survive a
// scheduled plan change — and the answer must come from one rule.
export function isInEffect(addOn, at = Date.now()) {
  if (addOn.state !== 'active' && addOn.state !== 'scheduled_for_removal') return false
  return !addOn.endsAt || new Date(addOn.endsAt).getTime() > at
}

// Add-on records currently granting capacity for a resource, oldest first.
export function inEffectAddOnsFor(subscription, key) {
  return (get(subscription, ['addOns'], []) || []).filter((a) => a.resource === key && isInEffect(a))
}

// Add-on records for a resource that are neither removed nor winding down.
//
// This is a narrower set than `inEffectAddOnsFor`, and the difference matters
// twice. Scheduling a removal deletes the Stripe item immediately (proration
// off, so no refund) and keeps the local record alive only to hold entitlement
// until `endsAt`. So a scheduled record: still grants capacity today (it belongs
// in the entitlement set), is already off the next invoice (it must NOT count
// toward the renewal), and can't be scheduled again (the API treats a repeat as
// a no-op, so it can't be part of a further reduction).
export function activeAddOnsFor(subscription, key) {
  return inEffectAddOnsFor(subscription, key).filter((a) => a.state === 'active')
}

// Units already purchased via an add-on for a resource (0 when none).
export function existingAddOnUnitsFor(subscription, key) {
  return inEffectAddOnsFor(subscription, key).reduce((sum, a) => sum + (a.effectiveUnits || 0), 0)
}

// Units from add-ons that are still BILLING — i.e. that renew. Narrower than
// `existingAddOnUnitsFor`, which also counts anything winding down.
export function activeAddOnUnitsFor(subscription, key) {
  return activeAddOnsFor(subscription, key).reduce((sum, a) => sum + (a.effectiveUnits || 0), 0)
}

// Packs (not units) that are still billing — the quantity a reduction targets.
export function activeAddOnPacksFor(subscription, key) {
  return activeAddOnsFor(subscription, key).reduce((sum, a) => sum + (a.quantity || 0), 0)
}

// The stepper's baseline: the resource total the plan RENEWS at, i.e. the plan's
// base limit plus every add-on that is still billing.
//
// Deliberately NOT the customer's current entitlement. The two differ only when
// something is scheduled for removal, and there the current figure is the wrong
// number to build a purchase on: that capacity is already off the next invoice,
// so a customer who removed a 4-pack and now wants 2 packs is choosing a level
// on top of the base limit, not on top of a total that is about to disappear.
// Anchoring here is what makes "remove it all, then add back less" reach the
// level they asked for instead of stacking on top of the removal.
//
// `AddOnSection` names the wind-down and its date separately, so the difference
// is never silent.
export function renewalUnitsFor(subscription, key) {
  return baseUnitsFor(subscription, key) + activeAddOnUnitsFor(subscription, key)
}

// The lowest total the stepper can reach: every active pack given up. Normally
// the plan's base limit, computed by subtraction rather than assumed so a record
// bought under a since-changed catalog increment still lands on a total the
// server can actually deliver.
export function removalFloorFor(subscription, key) {
  return renewalUnitsFor(subscription, key) - activeAddOnPacksFor(subscription, key) * incrementFor(key)
}

// The recurring cost, in cents, that reducing to `targetPacks` takes off the
// next invoice.
//
// Consumes the newest purchases first, mirroring `AddOns.plan_reduction/3` — a
// customer who bought the same resource twice holds two records at their own
// purchase-time rates, so which packs go decides the amount.
export function reductionCentsFor(subscription, key, targetPacks) {
  let remaining = activeAddOnPacksFor(subscription, key) - targetPacks

  return activeAddOnsFor(subscription, key)
    .slice()
    .reverse()
    .reduce((cents, addOn) => {
      if (remaining <= 0) return cents

      const taken = Math.min(addOn.quantity || 0, remaining)
      remaining -= taken
      return cents + taken * get(addOn, ['unitPrice', 'amount'], 0)
    }, 0)
}

// What the customer has right now, including capacity that is winding down.
// Prefers the server-composed effective limit so it matches the number the usage
// counters show; the local sum is the fallback.
export function currentUnitsFor(subscription, key) {
  const meta = resourceMeta(key)
  if (!meta) return 0

  const effective = get(subscription, ['effectiveLimits', meta.planField], null)
  if (effective != null) return effective

  return baseUnitsFor(subscription, key) + existingAddOnUnitsFor(subscription, key)
}

// The number portion of an amount (compact for pageviews) — no unit noun.
export function unitAmount(key, n) {
  const value = Number(n || 0)
  return key === 'pageviews' ? compactThousands(value) : value.toLocaleString('en-US')
}

// The unit noun for a resource (e.g. "Kits", "Custom Icons", "Pageviews", "GB").
export function unitNoun(key, n) {
  switch (key) {
    case 'kits':
      return Number(n) === 1 ? 'Kit' : 'Kits'
    case 'icon_uploads':
      return 'Custom Icons'
    case 'pageviews':
      return 'Pageviews'
    case 'bandwidth':
      return 'GB'
    default:
      return ''
  }
}

// User-facing amount string incl. the unit noun (e.g. "15 Kits",
// "400 Custom Icons", "250K Pageviews", "100 GB"). Pageviews render compact
// (thousands/millions) so the numbers stay short. An on/off add-on has no
// amount, so it reads as its name: "Pro+".
export function formatUnits(key, n) {
  if (isBinaryFor(key)) return resourceLabel(key)

  const noun = unitNoun(key, n)
  const amount = unitAmount(key, n)
  return noun ? `${amount} ${noun}` : amount
}

// Resources measured in units (GB) rather than counted by their own name
// (Kits). Copy has to treat the two oppositely, which is what the two helpers
// below are for.
const MEASURED_IN_UNITS = ['bandwidth']

// The amount for a sentence that already names the resource: the number alone,
// keeping only a unit the sentence can't supply — "your Kits will drop to 20",
// but "your Bandwidth will drop to 5 GB".
export function bareAmount(key, n) {
  return MEASURED_IN_UNITS.includes(key) ? formatUnits(key, n) : unitAmount(key, n)
}

// The amount for a sentence that doesn't name the resource, so the amount has
// to: "Add 5 Kits", and "Add 5 GB Bandwidth" where the unit alone wouldn't say
// what was counted.
export function namedAmount(key, n) {
  if (isBinaryFor(key)) return resourceLabel(key)

  return MEASURED_IN_UNITS.includes(key) ? `${formatUnits(key, n)} ${resourceLabel(key)}` : formatUnits(key, n)
}

// An ISO date as the sections render it: "Aug 7, 2026". Empty for a missing
// date, so a section with no `endsAt` yet renders nothing rather than
// "Invalid Date". Shared by every section that names a wind-down date, so the
// Seats and add-on messages can't drift into two date formats. Read in UTC, the
// way the server formats the same date for the cart and the receipt email: a
// removal ending 05:44 UTC on Nov 16 read as Nov 15 in Chicago, so the section
// and the receipt disagreed by a day.
export function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}

// 250000 → "250K"; 1500000 → "1.5M". Keeps big pageview counts legible.
export function compactThousands(n) {
  const value = Number(n || 0)
  if (value >= 1000000) {
    return `${(value / 1000000).toLocaleString('en-US', { maximumFractionDigits: 2 })}M`
  }
  return `${(value / 1000).toLocaleString('en-US')}K`
}

// The section keys the accordion can pre-expand via `?section=`. Seats is a
// first-class section alongside the add-on resources (D2/D3).
export const SEATS_SECTION_KEY = 'seats'

export function isValidSection(key) {
  return key === SEATS_SECTION_KEY || ADD_ON_RESOURCES.some((r) => r.key === key)
}

// Every route into the add-ons page. `?section=` names the accordion
// section(s) to pre-expand — one key, several comma-separated, or none at all
// for the page's own default. Pass a key so the customer lands on the thing
// they clicked about rather than on a page of collapsed sections.
export function addOnSectionRoute(sections) {
  const keys = [].concat(sections || []).filter(Boolean)

  return { name: 'account.add-on', query: keys.length ? { section: keys.join(',') } : {} }
}

// Seats above this route to Enterprise via support — no self-serve, on any
// plan. The contact link names the same number, so the two live together
// rather than drifting apart in the two surfaces that sell seats.
export const SEAT_MAX = 250
export const SEATS_CONTACT_URL = `mailto:hello@fontawesome.com?subject=I need a plan for more than ${SEAT_MAX} seats`
